import { HTMLTable } from '@blueprintjs/core'
import React, { Fragment, useState, useEffect, useMemo, useCallback, CSSProperties } from 'react'
import { fireOnSelectChange, resolveAllCheckedState, selectRow } from './select'
import { TableBottomSection } from './table_bottom_section'
import { DefaultSelected, getCellValue, getHeader, stickyStyles } from './common'
import { Params } from './useTableHooks'
import { Pagination } from './Pagination'
import { useUser } from '../../../hooks/useUser'
import { ClassValue } from 'classnames/types'
import classNames from 'classnames'
import { Column, ExtraType } from '../../../types/table'

interface TableProps<T> {
    loading?: boolean
    pagination?: Pagination
    params?: Params
    setParams?: any
    columns: Column<T>[]
    items?: T[]
    accordion?: any
    accordionRaw?: any // if true, we won't wrap the accordion content
    className?: any
    condensed?: boolean
    striped?: boolean
    empty?: any
    paginationClassname: any
    disableDefaultHtmlTableWrapperStyle?: boolean
    disableDefaultHtmlTableStyle?: boolean
    canSelectFromAllPages?: boolean
    reload?: any
    mutateItems?: any
    onSelectChange: any
    selectKey: any
    selectedValues?: any
    canSelect?: any
    showPerPage?: boolean
    perPage?: number
    setPerPage?: any
    perPageLabel?: string
    stickyHeader?: boolean
    stickyFooter?: boolean
    stickyFooterOffset?: number
    totals?: any
    enableRowClick?: boolean
    onRowClickItem?: (item: any) => void
    height?: number
    htmlTableWrapperClassname?: ClassValue
    hoverCheckbox?: boolean
    rowDividers?: boolean
}

export const MyHTMLTable = <T,>({
    loading,
    pagination,
    params = {},
    setParams,
    columns,
    items,
    accordion,
    accordionRaw, // if true, we won't wrap the accordion content
    className,
    condensed = true,
    striped = true,
    empty,
    paginationClassname,
    disableDefaultHtmlTableWrapperStyle = false,
    disableDefaultHtmlTableStyle = false,
    reload,
    mutateItems,
    canSelectFromAllPages = true,

    onSelectChange = () => {},
    selectKey = 'id',
    selectedValues = DefaultSelected,
    canSelect,
    showPerPage = false, // if true, display per page component
    perPage = 15, // display 15 items per page
    setPerPage,
    perPageLabel = 'per page',

    stickyHeader = false,
    stickyFooter = false,
    stickyFooterOffset = 0,
    totals,
    enableRowClick = false,
    onRowClickItem,
    height,
    htmlTableWrapperClassname,
    hoverCheckbox = false,
    rowDividers = false,
}: TableProps<T>): JSX.Element => {
    const { screenshotMode } = useUser()
    const [active, setActive] = useState<{ [key: number]: boolean }>({})
    const [showSelectAllWidget, setShowSelectAllWidget] = useState(false)
    const { stickyHeader: stickyHeaderSection, stickyFooter: stickyFooterSection } = stickyStyles(
        stickyHeader,
        stickyFooter,
        stickyFooterOffset,
    )

    const useSelect = !!onSelectChange

    const allCheckedState = useMemo(() => {
        return resolveAllCheckedState(items, selectedValues, selectKey, canSelect)
    }, [selectedValues, items])

    // Emit onSelectChange event
    useEffect(() => fireOnSelectChange(selectedValues, onSelectChange), [selectedValues])

    useEffect(() => {
        if (setParams) {
            setParams({ ...params, perPage: perPage })
        }
    }, [perPage])

    let cols: Column<T>[] = columns
    if (useSelect) {
        cols = [
            selectRow(
                canSelect,
                canSelectFromAllPages,
                showSelectAllWidget,
                setShowSelectAllWidget,
                selectedValues,
                allCheckedState,
                pagination,
                items,
                selectKey,
                onSelectChange,
                {},
                hoverCheckbox,
            ),
            ...columns,
        ]
    }

    const head = useCallback(() => {
        return cols.map((column, i) => {
            const headerStyle = typeof column !== 'string' ? column.headerStyle : undefined
            return (
                <th key={i} style={{ verticalAlign: 'middle', ...headerStyle }}>
                    {getHeader(column, params.sort, setParams, loading, headerStyle)}
                </th>
            )
        })
    }, [columns, showSelectAllWidget, allCheckedState, params.sort, items])

    const renderTotals = () => {
        let first = true
        return cols.map((column, i) => {
            const k = typeof column === 'string' ? column : column.key
            let v = totals[k]
            if (!v) {
                return <th key={i} />
            }

            if (typeof column !== 'string' && column.formatter) {
                v = column.formatter(v)
            }

            if (first) {
                v = ['Total: ', v]
                first = false
            }

            return (
                <th key={i} style={{ verticalAlign: 'middle' }}>
                    {v}
                </th>
            )
        })
    }

    const getRow = useCallback(
        (item: T, i: number, extra: ExtraType): JSX.Element => {
            return (
                <Fragment key={i}>
                    <tr
                        key={i}
                        onClick={(e) => {
                            if (onRowClickItem) {
                                // Don't trigger if clicking a link, button, or input inside the row
                                const target = e.target as HTMLElement
                                if (
                                    !target.closest(
                                        'a, button, input, select, textarea, label, [data-no-row-click]',
                                    )
                                ) {
                                    onRowClickItem(item)
                                }
                            }
                            onRowClick(i)
                        }}
                        className={classNames(
                            accordion && active[i] ? 'row-expanded' : '',
                            (hoverCheckbox || !!onRowClickItem) && 'group',
                            onRowClickItem && 'cursor-pointer',
                            rowDividers &&
                                'border-b border-[#EDF0F3] hover:bg-[#FAFBFC] hover:shadow-[inset_0_1px_0_rgba(14,41,71,0.05),inset_0_-1px_0_rgba(14,41,71,0.05)] transition-all duration-100',
                        )}
                    >
                        {cols.map((column, i) => (
                            <td
                                key={i}
                                style={{
                                    verticalAlign: 'middle',
                                    ...(typeof column !== 'string' ? column.style : undefined),
                                }}
                                {...(typeof column !== 'string' && column.noRowClick
                                    ? { 'data-no-row-click': true }
                                    : {})}
                            >
                                <span className={loading ? 'bp4-skeleton' : ''}>
                                    {getCellValue(column, item, extra)}
                                </span>
                            </td>
                        ))}
                    </tr>
                    {active[i] && getAccordion(item, i)}
                </Fragment>
            )
        },
        [accordion, loading, active, selectedValues, items, columns],
    )

    const body = useMemo(() => {
        return (
            items?.map((item: T, i: number) =>
                getRow(item, i, {
                    screenshotMode,
                    reload,
                    mutateItems,
                    // props: componentProps, //@todo
                }),
            ) ?? []
        )
    }, [
        accordion,
        loading,
        active,
        selectedValues,
        items,
        columns,
        screenshotMode,
        reload,
        mutateItems,
    ])

    const handleRowClick = (rowIndex: number) => {
        setActive((prev) => ({ ...prev, [rowIndex]: !prev[rowIndex] }))
    }

    const onRowClick = useCallback(
        (rowIndex: number) => {
            return enableRowClick ? handleRowClick(rowIndex) : null
        },
        [enableRowClick],
    )

    const getAccordion = (item: T, i: number) => {
        if (!accordion) {
            return null
        }

        const content = accordion(item)

        if (!content) {
            return null
        }

        if (accordionRaw) {
            return accordion(item)
        }

        return (
            <tr key={i + '-expanded'}>
                <td colSpan={100}>{accordion(item)}</td>
            </tr>
        )
    }

    const tHead = useMemo(
        () => (
            <thead>
                <tr className="bg-white table-header-sticky" style={{ ...stickyHeaderSection }}>
                    {head()}
                </tr>
                {!!totals && <tr className="bg-white table-totals">{renderTotals()}</tr>}
            </thead>
        ),
        [columns, showSelectAllWidget, allCheckedState, params.sort, items],
    )

    const tBody = <tbody>{body}</tbody>

    const tableBottomSection = useMemo(() => {
        return (
            <TableBottomSection
                pagination={pagination}
                paginationClassname={paginationClassname}
                stickyFooterSection={stickyFooterSection}
                loading={loading}
                showPerPage={showPerPage}
                perPage={perPage}
                setPerPage={setPerPage}
                perPageLabel={perPageLabel}
                params={params}
                setParams={setParams}
            />
        )
    }, [pagination, loading, perPage, perPageLabel, stickyFooterSection])

    const styles: CSSProperties = {
        width: '100%',
        overflowX: 'auto',
    }
    if (height) {
        styles.height = height + 'px'
    }

    return (
        <div
            className={classNames(htmlTableWrapperClassname, {
                'w-100': !disableDefaultHtmlTableWrapperStyle,
            })}
        >
            <div id="html-table-wrapper" style={styles}>
                <HTMLTable
                    // @ts-ignore
                    style={{ overflowX: 'auto' }}
                    className={className + ' ' + (disableDefaultHtmlTableStyle ? '' : 'w-100')}
                    condensed={condensed}
                    interactive={!!accordion}
                    striped={striped}
                >
                    {tHead}
                    {tBody}
                    {!loading && (!items || items.length === 0) && empty}
                </HTMLTable>
            </div>
            {tableBottomSection}
        </div>
    )
}
